/**
 * Icon Configuration - Dimensions and scale factors for different icon types
 *
 * This module defines the visual configuration for each icon type including
 * display size, scale factor, icon dimensions, and positioning ratios.
 */

import { IconConfig, IconType } from '@/components/icons/nodeIcons/types';

/**
 * Get icon configuration by type
 *
 * Each icon type has specific dimensions, scale factors, and positioning
 * parameters optimized for visual clarity and consistency.
 *
 * @param type - The icon type to get configuration for
 * @returns Icon configuration with all necessary parameters
 */
export const getIconConfig = (type: IconType): IconConfig => {
  switch (type) {
    case 'computer':
      return {
        displaySize: 64,
        scale: 2,
        iconWidth: 28,
        iconHeight: 18,
        iconScaleRatio: 0.4,
        labelOffsetRatio: 0.08,
        labelYOffsetRatio: 0.15,
        fontSizeRatio: 0.1,
      };

    case 'attribute':
    case 'technology':
      return {
        displaySize: 80,
        scale: 3,
        iconWidth: 16,
        iconHeight: 16,
        iconScaleRatio: 0.4,
        labelOffsetRatio: 0.08,
        labelYOffsetRatio: 0.15,
        fontSizeRatio: 0.1,
      };

    case 'seed':
      return {
        displaySize: 64,
        scale: 2,
        iconWidth: 25,
        iconHeight: 31,
        iconScaleRatio: 0.5,
        labelOffsetRatio: 0.08,
        labelYOffsetRatio: 0.15,
        fontSizeRatio: 0.1,
      };

    case 'risk':
      return {
        displaySize: 80,
        scale: 3,
        iconWidth: 12,
        iconHeight: 14,
        iconScaleRatio: 0.38,
        labelOffsetRatio: 0.08,
        labelYOffsetRatio: 0.15,
        fontSizeRatio: 0.1,
      };

    case 'tag':
    case 'webpage':
    case 'user':
    case 'group':
    case 'domain':
      return {
        displaySize: 80,
        scale: 3,
        iconWidth: 24,
        iconHeight: 24,
        iconScaleRatio: 0.7,
        labelOffsetRatio: 0,
        labelYOffsetRatio: 0,
        fontSizeRatio: 0.1,
      };

    case 'cloud':
      return {
        displaySize: 80,
        scale: 3,
        iconWidth: 28,
        iconHeight: 20,
        iconScaleRatio: 0.65,
        labelOffsetRatio: 0,
        labelYOffsetRatio: 0,
        fontSizeRatio: 0.1,
      };

    default:
      // Default fallback configuration
      return {
        displaySize: 80,
        scale: 3,
        iconWidth: 16,
        iconHeight: 16,
        iconScaleRatio: 0.4,
        labelOffsetRatio: 0.08,
        labelYOffsetRatio: 0.15,
        fontSizeRatio: 0.1,
      };
  }
};
